import { http, HttpResponse, passthrough } from 'msw';
import type { JsonBodyType, RequestHandler } from 'msw';
import range from 'lodash/range';

import { DemoProfile, EntityType, Usertype } from '../types';
import { getStatePaymentPortalUrl, getStateTaxInfo, resolveStateCode, scaleStateTax } from '../stateTax';
import { getDemoScheduledCall } from '../scheduledCall';
import { CONTENT as DATED_CONTENT } from '../utils';

import { setDemoMember } from './demoMember';

import config from 'modules/common/config';
import { getCurrentQTE } from 'modules/Tax/utils/dates';
import type { TaxJurisdictionInfo } from 'services/tax/queries/useTaxJurisdictionInfo/useTaxJurisdictionInfo.interface';
import { buildOAuthResponse } from 'tests/storybook/handlers/oauth';

type BookkeepingBlob = {
    bank_accounts?: JsonBodyType[];
    transactions?: JsonBodyType[];
    accountable_plans?: JsonBodyType[];
    out_of_pocket?: JsonBodyType[];
    [key: string]: unknown;
};

type ContentBlob = {
    dashboard?: JsonBodyType;
    bookkeeping?: BookkeepingBlob;
    tax?: JsonBodyType;
    payroll?: JsonBodyType;
    documents?: JsonBodyType;
    member?: Record<string, unknown>;
};

// Chart of accounts is the same across every profile — these are stable categorization
// targets a member sees in the transactions UI. Per-profile JSON only controls which
// ones a transaction *references* by id, not the catalog itself.
const DEMO_GL_ACCOUNTS = [
    {
        id: 'gla_consulting',
        name: 'Consulting Revenue',
        account_no: '4000',
        type: 'INCOME',
        status: 'active',
        level: 1,
        has_children: false,
        requires_receipt: false,
        is_categorizable: true,
        allow_filtering: true,
        alias_inflow: 'Consulting Received',
        alias_outflow: 'Consulting Refund Issued',
    },
    {
        id: 'gla_office',
        name: 'Office Expense',
        account_no: '6100',
        type: 'EXPENSE',
        status: 'active',
        level: 1,
        has_children: false,
        requires_receipt: false,
        is_categorizable: true,
        allow_filtering: true,
    },
    {
        id: 'gla_software',
        name: 'Software & Subscriptions',
        account_no: '6300',
        type: 'EXPENSE',
        status: 'active',
        level: 1,
        has_children: false,
        requires_receipt: false,
        is_categorizable: true,
        allow_filtering: true,
    },
    {
        id: 'gla_travel',
        name: 'Travel',
        account_no: '6400',
        type: 'EXPENSE',
        status: 'active',
        level: 1,
        has_children: false,
        requires_receipt: false,
        is_categorizable: true,
        allow_filtering: true,
    },
    {
        id: 'gla_meals',
        name: 'Meals & Entertainment',
        account_no: '6200',
        type: 'EXPENSE',
        status: 'active',
        level: 1,
        has_children: false,
        requires_receipt: false,
        is_categorizable: true,
        allow_filtering: true,
    },
    {
        id: 'gla_advertising',
        name: 'Advertising & Marketing',
        account_no: '6500',
        type: 'EXPENSE',
        status: 'active',
        level: 1,
        has_children: false,
        requires_receipt: false,
        is_categorizable: true,
        allow_filtering: true,
    },
    {
        id: 'gla_equipment',
        name: 'Computer & Equipment',
        account_no: '6600',
        type: 'EXPENSE',
        status: 'active',
        level: 1,
        has_children: false,
        requires_receipt: false,
        is_categorizable: true,
        allow_filtering: true,
    },
    {
        id: 'gla_contractors',
        name: 'Contractor & Professional Services',
        account_no: '6700',
        type: 'EXPENSE',
        status: 'active',
        level: 1,
        has_children: false,
        requires_receipt: false,
        is_categorizable: true,
        allow_filtering: true,
    },
    {
        id: 'gla_education',
        name: 'Professional Development',
        account_no: '6750',
        type: 'EXPENSE',
        status: 'active',
        level: 1,
        has_children: false,
        requires_receipt: false,
        is_categorizable: true,
        allow_filtering: true,
    },
    {
        id: 'gla_telephone',
        name: 'Telephone & Internet',
        account_no: '6800',
        type: 'EXPENSE',
        status: 'active',
        level: 1,
        has_children: false,
        requires_receipt: false,
        is_categorizable: true,
        allow_filtering: true,
    },
    {
        id: 'gla_dues',
        name: 'Dues & Subscriptions',
        account_no: '6350',
        type: 'EXPENSE',
        status: 'active',
        level: 1,
        has_children: false,
        requires_receipt: false,
        is_categorizable: true,
        allow_filtering: true,
    },
    {
        id: 'gla_bank_fees',
        name: 'Bank Charges',
        account_no: '6900',
        type: 'EXPENSE',
        status: 'active',
        level: 1,
        has_children: false,
        requires_receipt: false,
        is_categorizable: true,
        allow_filtering: true,
    },
    {
        id: 'gla_retirement',
        name: 'Retirement Contributions',
        account_no: '7000',
        type: 'EXPENSE',
        status: 'active',
        level: 1,
        has_children: false,
        requires_receipt: false,
        is_categorizable: true,
        allow_filtering: true,
    },
    {
        id: 'gla_distribution',
        name: 'Shareholder Distribution',
        account_no: '36000',
        type: 'EQUITY',
        status: 'active',
        level: 1,
        has_children: false,
        requires_receipt: false,
        is_categorizable: false,
        allow_filtering: false,
    },
    {
        id: 'gla_uncategorized',
        name: 'Ask My Accountant',
        account_no: '9000',
        type: 'EXPENSE',
        status: 'active',
        level: 1,
        has_children: false,
        requires_receipt: false,
        is_categorizable: true,
        allow_filtering: true,
    },
];

/**
 * Per-session in-memory state for the bookkeeping endpoints that accept writes.
 * Initialized from the per-profile JSON blob via deep clone, then mutated in place
 * by the update/create/delete handlers. Lives in the closure of `buildOverrideHandlers`,
 * which means it survives SPA navigation but resets on a hard page reload — that's the
 * intended "demo session" lifetime.
 */
interface DemoBookkeepingState {
    transactions: Array<Record<string, unknown>>;
    accountable_plans: Array<Record<string, unknown>>;
    out_of_pocket: Array<Record<string, unknown>>;
}

// Mixed-use cost/mileage fields — used to detect an empty month and copy prior-month values in.
const MIXED_USE_VALUE_FIELDS = [
    'cost_security',
    'cost_cleaning',
    'cost_utilities',
    'cost_HOA',
    'cost_home_insurance',
    'cost_property_taxes',
    'cost_mortgage_interest',
    'cost_rent',
    'cost_rent_insurance',
    'miles_driven_total',
    'miles_driven_business',
    'cost_internet',
    'cost_phone',
];

const periodIndex = (year: number, month: number) => year * 12 + (month - 1); // month is 1-12
const fromPeriodIndex = (idx: number) => ({ year: Math.floor(idx / 12), month: (idx % 12) + 1 });
const isEmptyPlan = (plan: Record<string, unknown>) =>
    MIXED_USE_VALUE_FIELDS.every((f) => plan[f] === undefined || Number(plan[f]) === 0);

type Plan = Record<string, unknown>;

/**
 * Re-anchors seeded reimbursement data to the current month: shifts every plan + OOP item so the
 * latest seeded month becomes now, rewrites period-encoded uuids, and fills the current
 * (in-progress) month from the prior one so the default view isn't all zeros. Returns new arrays.
 */
function remapReimbursementsToNow(
    plans: Plan[],
    outOfPocket: Plan[]
): { accountablePlans: Plan[]; outOfPocket: Plan[] } {
    if (!plans.length) {
        return { accountablePlans: plans, outOfPocket };
    }
    const latestIdx = plans.reduce((max, p) => Math.max(max, periodIndex(Number(p.year), Number(p.month))), 0);
    const now = new Date();
    const delta = periodIndex(now.getFullYear(), now.getMonth() + 1) - latestIdx;

    // Shift each plan's period + uuid (ap_scorp_2026_05 → _06); track old→new for OOP repointing.
    const uuidByOld: Record<string, string> = {};
    const shiftedPlans = plans.map((p) => {
        const shifted = fromPeriodIndex(periodIndex(Number(p.year), Number(p.month)) + delta);
        const mm = String(shifted.month).padStart(2, '0');
        const newUuid = String(p.uuid).replace(/_\d{4}_\d{2}$/, `_${shifted.year}_${mm}`);
        uuidByOld[String(p.uuid)] = newUuid;
        return { ...p, uuid: newUuid, year: shifted.year, month: shifted.month };
    });

    // Fill the current (empty, in-progress) month from the prior one so it shows real numbers.
    const ordered = [...shiftedPlans].sort(
        (a, b) => periodIndex(Number(a.year), Number(a.month)) - periodIndex(Number(b.year), Number(b.month))
    );
    const orderedIndex = new Map(ordered.map((plan, i) => [plan, i]));
    const accountablePlans = shiftedPlans.map((plan) => {
        const i = orderedIndex.get(plan) ?? 0;
        if (i === 0 || !isEmptyPlan(plan)) {
            return plan;
        }
        const prev: Plan = ordered[i - 1];
        const fills: Plan = {};
        [...MIXED_USE_VALUE_FIELDS, 'total_reimbursements'].forEach((f) => {
            if (prev[f] !== undefined) {
                fills[f] = prev[f];
            }
        });
        return { ...plan, ...fills };
    });

    // Shift OOP dates by the same delta and repoint to the shifted plan uuid.
    const shiftedOutOfPocket = outOfPocket.map((o) => {
        const next: Plan = { ...o };
        const ref = o.accountable_plan_uuid as string | undefined;
        if (ref && uuidByOld[ref]) {
            next.accountable_plan_uuid = uuidByOld[ref];
        }
        const raw = o.date as string | undefined;
        if (raw) {
            const d = new Date(raw);
            const shifted = fromPeriodIndex(periodIndex(d.getUTCFullYear(), d.getUTCMonth() + 1) + delta);
            const mm = String(shifted.month).padStart(2, '0');
            // Clamp the day so a 31st shifting into a shorter month stays a valid date.
            const lastDayOfMonth = new Date(Date.UTC(shifted.year, shifted.month, 0)).getUTCDate();
            const dd = String(Math.min(d.getUTCDate(), lastDayOfMonth)).padStart(2, '0');
            next.date = `${shifted.year}-${mm}-${dd}T00:00:00Z`;
        }
        return next;
    });

    return { accountablePlans, outOfPocket: shiftedOutOfPocket };
}

function initBookkeepingState(blob: BookkeepingBlob | undefined): DemoBookkeepingState {
    // JSON.parse/stringify deep-clones so subsequent mutations don't bleed into the
    // imported JSON modules (which Vite may freeze).
    const clone = <T>(value: T): T => JSON.parse(JSON.stringify(value)) as T;
    const { accountablePlans, outOfPocket } = remapReimbursementsToNow(
        clone((blob?.accountable_plans ?? []) as Plan[]),
        clone((blob?.out_of_pocket ?? []) as Plan[])
    );
    return {
        transactions: clone((blob?.transactions ?? []) as Array<Record<string, unknown>>),
        accountable_plans: accountablePlans,
        out_of_pocket: outOfPocket,
    };
}

function summarizeTransactions(transactions: JsonBodyType[]) {
    const typeCounts = new Map<string, { count: number; total: number }>();
    const reviewCounts = new Map<string, { count: number; total: number }>();
    let totalAmount = 0;
    transactions.forEach((raw) => {
        const tx = raw as { type: string; review_status: string; amount: string };
        const amount = Number(tx.amount);
        totalAmount += amount;
        const t = typeCounts.get(tx.type) ?? { count: 0, total: 0 };
        typeCounts.set(tx.type, { count: t.count + 1, total: t.total + amount });
        const r = reviewCounts.get(tx.review_status) ?? { count: 0, total: 0 };
        reviewCounts.set(tx.review_status, { count: r.count + 1, total: r.total + amount });
    });
    const toGroups = (map: Map<string, { count: number; total: number }>) =>
        Array.from(map.entries()).map(([value, { count, total }]) => ({
            value,
            display_name: value,
            count,
            total_amount: total.toFixed(2),
        }));
    return {
        total_count: transactions.length,
        total_amount: totalAmount.toFixed(2),
        facets: [
            { name: 'TYPE', groups: toGroups(typeCounts) },
            { name: 'REVIEW_STATUS', groups: toGroups(reviewCounts) },
        ],
    };
}

function getDjangoOrigin(): string {
    const raw = config.djangoURL ?? 'http://localhost:8000';
    try {
        return new URL(raw).origin;
    } catch {
        return raw.replace(/\/$/, '');
    }
}

// Transactions are re-dated to the current month there, so the Transactions tab and the
// dashboard figures derived from the same ledger agree.
const CONTENT: Record<EntityType, ContentBlob> = {
    scorp: DATED_CONTENT.scorp as ContentBlob,
    llc: DATED_CONTENT.llc as ContentBlob,
};

const DEMO_MEMBER_UUID = 'demo-member-uuid';
const DEMO_GUSTO_COMPANY_UUID = 'demo-gusto-company';
// The member's named specialist, plus an inert stand-in for the Calendly booking URL. It is never
// fetched or navigated to — `MessageCenterFooter` just needs a non-empty `schedule_url` to render the
// CTA, and the demo's aliased `react-calendly` (demo/stubs/reactCalendly.tsx) opens an in-app calendar
// instead of a Calendly page. Deliberately not a real-looking URL so it can't be mistaken for one.
const DEMO_SPECIALIST_NAME = 'Jordan Reyes';
const DEMO_SCHEDULE_URL = 'demo-scheduler://member-relationship-manager';

// Bank accounts as the content blob stores them (`bookkeeping.bank_accounts`), narrowed to the
// fields the Plaid-items shape needs. `connection_lost` marks the institution whose feed dropped —
// the one the "syncing connection was lost" task card sends the member to fix.
type DemoBankAccount = {
    id: string;
    name: string;
    api_name?: string;
    mask?: string;
    account_type?: string;
    institution_name?: string;
    connection_lost?: boolean;
};

// Real Plaid institution ids, so Settings → Bank accounts renders the actual bank logos
// (`insLogoMapping` in modules/common/components/Icons/BankLogos keys off these) instead of the
// generic fallback icon. Unlisted institutions fall back to that icon, same as prod.
const PLAID_INSTITUTION_IDS: Record<string, string> = {
    'American Express': 'ins_10',
    'Bank of America': 'ins_127989',
    Chase: 'ins_56',
    Citibank: 'ins_5',
    'PNC Bank': 'ins_13',
    Relay: 'ins_117228',
    'TD Bank': 'ins_14',
    'Wells Fargo': 'ins_127991',
};

function subTypeFor(account: DemoBankAccount): string {
    if (account.account_type === 'CREDIT') {
        return 'CREDIT_CARD';
    }
    return account.api_name?.includes('SAVINGS') ? 'SAVINGS' : 'CHECKING';
}

/**
 * Content bank accounts → `GET hurdlr/member/plaid-items/` (Settings → Bank accounts). One Plaid item
 * per institution, so two accounts at the same bank group under one card the way prod renders them.
 */
function buildPlaidItems(blob: ContentBlob) {
    const accounts = (blob.bookkeeping?.bank_accounts ?? []) as DemoBankAccount[];
    const institutions = [...new Set(accounts.map((account) => account.institution_name ?? 'Bank'))];

    return institutions.map((institutionName, index) => {
        const owned = accounts.filter((account) => (account.institution_name ?? 'Bank') === institutionName);
        const isLost = owned.some((account) => account.connection_lost);
        const institutionId =
            PLAID_INSTITUTION_IDS[institutionName] ?? `ins_demo_${institutionName.toLowerCase().replace(/\W+/g, '_')}`;
        return {
            id: index + 1,
            status: 'ACTIVE',
            apiItemId: `item_${institutionId}`,
            apiInstitutionId: institutionId,
            apiInstitutionName: institutionName,
            apiAccessToken: `access_${institutionId}`,
            // Plaid's real "the member must re-auth" signal — what BankAlerts keys off.
            apiErrorType: isLost ? 'ITEM_ERROR' : null,
            apiErrorCode: isLost ? 'ITEM_LOGIN_REQUIRED' : null,
            apiErrorMessage: isLost ? 'the login details of this item have changed' : null,
            lastSyncedDate: new Date().toISOString(),
            lastInstitutionTranDate: new Date().toISOString(),
            bank_accounts: owned.map((account, accountIndex) => ({
                id: index * 10 + accountIndex + 1,
                status: 'ACTIVE',
                apiName: 'PLAID',
                apiAccountId: account.id,
                apiAccountName: account.name,
                apiAccountNo: account.mask ?? '0000',
                apiInstitutionId: institutionId,
                autoClassify: 'BUSINESS',
                apiAccountType: account.account_type === 'CREDIT' ? 'CREDIT' : 'DEPOSITORY',
                apiSubType: subTypeFor(account),
                apiOfficialName: account.name,
                defaultClientId: 1,
                syncTransactions: true,
                lastExpenseSyncedDate: null,
                lastRevenueSyncedDate: null,
            })),
        };
    });
}

// Doc uuids whose AI pipeline was triggered (classify/extract POST on submit). The job GET returns
// "no job yet" until then, so the upload step shows instead of auto-validating on mount.
const classificationStartedFor = new Set<string>();
const extractionStartedFor = new Set<string>();

/**
 * Demo-specific MSW handlers. These layer over the Storybook baseline; whichever route
 * pattern matches first wins, and overrides are placed earlier in buildHandlers().
 */
const INCORPORATION_LABEL: Record<EntityType, string> = {
    scorp: 'S-Corp',
    llc: 'LLC',
};

const INCORPORATION_SUFFIX: Record<EntityType, string> = {
    scorp: 'Inc.',
    llc: 'LLC',
};

// Usertype for an activated member ({source}-{target}). Drives S-Corp/LLC gates like
// isSCorpMember() — the Reimbursement tab's Accountable Plan only renders for S-Corp.
const ACTIVATED_USERTYPE: Record<EntityType, Usertype> = {
    scorp: 'SC-SC',
    llc: 'LLC-LLC',
};

export function buildOverrideHandlers(profile: DemoProfile): RequestHandler[] {
    const blob = CONTENT[profile.entity_type];
    const isOnboarding = profile.view === 'launchpad';
    const fullname = `${profile.first_name} ${profile.last_name}`;
    // The prospect's state, driving addresses, state of incorporation and the QTE.
    // Named `stateCode`/`stateInfo` to avoid colliding with the bookkeeping `state` below.
    const stateCode = resolveStateCode(profile.state);
    const stateInfo = getStateTaxInfo(stateCode);
    // The quarter the Taxes pages consider "current" — read from the same prod util they use, so the
    // mocked estimates always cover it and the page never falls back to its "no QTE yet" placeholder.
    const [demoQteYear, demoQteQuarter] = getCurrentQTE();
    // The formation flow reads *structured* addresses (`member.business_address`,
    // `member.home_address`) rather than the flat `businessaddress*`/`homeaddress*` fields
    // further down, so its screens render blank without these. Both shapes are built from
    // the same values so they can't drift.
    const businessAddress = {
        street: '123 Market St',
        unit: '',
        city: stateInfo.city,
        state: stateCode,
        zipcode: stateInfo.zip,
    };
    const homeAddress = {
        street: '500 Valencia St',
        unit: '',
        city: stateInfo.city,
        state: stateCode,
        zipcode: stateInfo.zip,
    };
    // `one_line_*` back the "same as ..." toggles, which compare the one-line forms.
    const toOneLine = (a: typeof businessAddress) => `${a.street}, ${a.city}, ${a.state} ${a.zipcode}`;
    /**
     * Build one quarter of the demo QTE, with the state line scaled to the prospect's state.
     *
     * The authored figures are California's; `scaleStateTax` rescales them by effective-rate
     * ratio, so California is unchanged and the nine no-income-tax states drop the state row
     * entirely (an empty `state_estimations` renders federal-only rather than a $0 line).
     */
    /**
     * State jurisdictions for a tax-return summary, scaled from the authored California
     * amount. Empty for the no-income-tax states so their returns show federal only.
     */
    const buildStateJurisdictions = (californiaAmountDue: number) => {
        const amountDue = scaleStateTax(californiaAmountDue, stateCode);
        if (!amountDue) {
            return [];
        }
        return [
            {
                jurisdiction: stateCode,
                amount_due: amountDue.toFixed(2),
                refund_amount: '0.00',
            },
        ];
    };
    const buildDemoQte = ({
        year,
        quarter,
        netIncome,
        grossWages,
        federal,
        californiaStateTax,
        status,
    }: {
        year: number;
        quarter: number;
        netIncome: string;
        grossWages: string;
        federal: number;
        californiaStateTax: number;
        status: string;
    }) => {
        const stateTax = scaleStateTax(californiaStateTax, stateCode);
        return {
            uuid: `qte_q${quarter}_${year}`,
            tax_id: 'tax_demo',
            net_income: netIncome,
            outside_income: null,
            gross_wages: grossWages,
            status,
            is_manual: false,
            federal_estimation: {
                amount: String(federal),
                outside_amount: null,
                total_amount: String(federal),
                breakdown: null,
            },
            state_estimations: stateTax
                ? [
                      {
                          state: stateCode,
                          total_amount: String(stateTax),
                          amount: String(stateTax),
                          outside_amount: null,
                          // null, not undefined: JSON.stringify drops undefined keys, which
                          // would give the state and federal estimations different shapes.
                          breakdown: null,
                      },
                  ]
                : [],
            business_estimations: [],
            quarter,
            year,
            estimated_federal_tax: String(federal),
            estimated_state_tax: String(stateTax),
            estimated_business_tax: '0',
            total_estimated: String(federal + stateTax),
            previous_qte: null,
            comments: [],
            recalculation_requests: [],
        };
    };
    // Mutable, per-session state. Read by list/search handlers, mutated by writes.
    const state = initBookkeepingState(blob.bookkeeping);
    let oopCounter = 0;
    const nextOopUuid = () => {
        oopCounter += 1;
        return `oop_session_${Date.now()}_${oopCounter}`;
    };
    const findGl = (id: string) => DEMO_GL_ACCOUNTS.find((g) => g.id === id);
    const member = {
        id: 1,
        uuid: DEMO_MEMBER_UUID,
        first_name: profile.first_name,
        last_name: profile.last_name,
        fullname,
        full_business_name: profile.organization,
        legal_first_name: profile.first_name,
        legal_middle_name: '',
        legal_last_name: profile.last_name,
        nickname: profile.first_name,
        preferred_name: profile.first_name,
        pronouns: [],
        email: profile.email,
        businessname: profile.organization,
        federal_business_name: profile.organization,
        company_name: profile.organization,
        profession: profile.profession,
        primaryactivity: profile.profession,
        primaryactivityother: '',
        incorporation: INCORPORATION_LABEL[profile.entity_type],
        incsuffix: INCORPORATION_SUFFIX[profile.entity_type],
        entity_type: profile.entity_type,
        status: 'OK',
        hasPaymentIssue: false,
        // Tier drives two things the demo cares about: the sidebar's Payroll item is core-only
        // (`payrollItemValidation`), so the LLC persona must not be core or Payroll appears for a
        // member who doesn't have it; and MessageCenterFooter renders nothing until a tier resolves,
        // then hides "Schedule a call with us" for Essential members only — so `free` keeps the CTA.
        membership_tier: profile.entity_type === 'scorp' ? 'core' : 'free',
        // Launchpad view → stage 'launchpad' (drives the launchpad + sidebar locks); else activated.
        // Must encode structure ({source}-{target}); a literal like 'member' fails isSCorpMember()
        // and hides the Accountable Plan. Activated members use the completed form.
        usertype: isOnboarding ? profile.journey : ACTIVATED_USERTYPE[profile.entity_type],
        stage: isOnboarding ? 'launchpad' : 'activated',
        is_onboarding_completed: !isOnboarding,
        firstpayment: '2024-01-15',
        lastpayment: '2026-04-15',
        // Must match the mocked 2553 extraction so the S Corp shareholder validator passes.
        SSN: '123-45-6789',
        // Formation forms validate these exact formats: dob MM/DD/YYYY, phone (XXX) XXX-XXXX.
        dob: '06/15/1985',
        citizenship: 'US',
        citizenshipcode: 'US',
        personalphonenumber: '(555) 010-0100',
        moving_out_state: false,
        clients_outside_state: false,
        business_operated_last_year: true,
        business_prior_years_itr_not_filled: false,
        business_activity_description: profile.profession,
        businessphonenumber: '(555) 010-0101',
        businesssetupamount: 0,
        businessaddress: '123 Market St',
        businessaddressnumber: '123',
        businessaddressstreet: 'Market St',
        businessaddresstype: 'street',
        businessaddressaptunit: '',
        businessaddresscity: stateInfo.city,
        businessaddressstate: stateCode,
        businessaddresszipcode: stateInfo.zip,
        homeaddressstreet: 'Valencia St',
        homeaddressnumber: '500',
        homeaddressaptunit: '',
        homeaddresscity: stateInfo.city,
        homeaddressstate: stateCode,
        homeaddresszipcode: stateInfo.zip,
        // Structured addresses the formation "Business address" / "Personal address" screens
        // actually read. `mailing_address: null` means "same as business", which is what makes
        // that toggle default to Yes.
        business_address: businessAddress,
        home_address: homeAddress,
        mailing_address: null,
        one_line_business_address: toOneLine(businessAddress),
        one_line_home_address: toOneLine(homeAddress),
        one_line_mail_address: toOneLine(businessAddress),
        has_new_address: false,
        // '---' = not married → marital toggle defaults to No and the spousal step is skipped.
        additionalshareholdername: '---',
        hasBusinessLicense: 'yes',
        establisheddate: '2022-01-01',
        EIN: '12-3456789',
        entitynumber: '',
        registeredagent: '',
        stateofoperation: stateCode,
        offboarding_call_link: '',
        bookkeeping_provider: 'hurdlr',
        created: '2022-01-01T00:00:00Z',
        updated: new Date().toISOString(),
        business: {
            member: 1,
            state_of_inc: {
                id: stateCode,
                is_servicing: true,
                name: stateInfo.name,
                // The remaining attributes are per-state formation rules the demo has no
                // authoritative source for, so they keep their (California) defaults. Only
                // community property, which the formation flow branches on, is personalized.
                attributes: {
                    ein_before_llc: false,
                    is_community_property: Boolean(stateInfo.isCommunityProperty),
                    wet_ink_signature_required: false,
                    online_formation_allowed: true,
                    naics_code_required: false,
                    publication_required: false,
                    state_scorp_form_required: false,
                    business_license_required: false,
                    passthrough_tax_eligible: true,
                },
                content: {
                    articles_of_organization_name: 'Articles of Organization',
                    additional_filing_doc_name: '',
                    llc_filing_website: '',
                    sos_business_name_check_website: '',
                    federal_scorp_fax_number: '',
                    state_scorp_fax_number: '',
                },
            },
            s_corp_election_date: profile.entity_type === 'scorp' ? '2022-03-15' : null,
            naics_code: '541510',
            naics_business_activity: profile.profession,
            industry: 'Professional Services',
            profession: profile.profession,
            prior_payroll_provider: '',
            activity_details: {},
            has_staff: false,
        },
        ...(blob.member ?? {}),
    };

    setDemoMember(member);

    return [
        // Identity — make every component think the visitor is the demo member.
        // NOTE: do NOT add `*/members/:id/` here. The dashboard hits paths like
        // `/v2/members/tasks/` (operator tasks) which a loose `:id` would swallow and
        // return the member object instead of an empty task list, crashing OperatorTaskBanner.
        http.get('*/members/', () => HttpResponse.json([member])),
        http.get('*/membershipinfo/', () => HttpResponse.json({ id: 1, status: 'active' })),
        http.get('*/rest-auth/user/', () =>
            HttpResponse.json({
                pk: 999001,
                id: 999001,
                email: profile.email,
                first_name: profile.first_name,
                last_name: profile.last_name,
                username: profile.email,
                is_employee: false,
                groups: [],
                isImpersonator: false,
                // Must match the value in demo/mockUser.ts. Layout.tsx loop-refetches the
                // user until `destination` is truthy.
                destination: '/dashboard',
            })
        ),

        // Misc identity-adjacent endpoints called early by the layout shell.
        // hurdlr.connected unlocks the Accounting + Invoicing sidebar items (accountingItemValidation
        // in Layout/Sidebar/menuAreas.ts). gusto.connected makes the prod Payroll module mount its
        // activated-member dashboard instead of the "Connect Gusto" zero state. Only the S-Corp
        // persona has payroll, so the other personas report Gusto as disconnected — this hides the
        // Payroll sidebar item to match the real product.
        http.get('*/oauth/', () =>
            HttpResponse.json(
                buildOAuthResponse({
                    hurdlr: { connected: true },
                    gusto:
                        profile.entity_type === 'scorp'
                            ? { connected: true, organization_name: profile.organization }
                            : { connected: false },
                })
            )
        ),
        http.get('*/members/:id/referral-prompt-eligibility/', () => HttpResponse.json({ eligible: false })),

        // Specialist calendars — drives the Message Center footer's "Schedule a call with us" CTA
        // (MessageCenterFooter picks `mrm` for activated members, `em` during onboarding). The
        // schedule_url only has to be non-empty for the CTA to render: the demo aliases
        // `react-calendly` to an in-app scheduler, so no Calendly page is ever loaded. Status flips
        // to Scheduled once the visitor books in that scheduler (see demo/scheduledCall.ts).
        http.get('*/member-calendly-links/', () => {
            const startTime = getDemoScheduledCall();
            const calendar = {
                status: startTime ? 'Scheduled' : 'Not Scheduled',
                start_time: startTime ?? '',
                end_time: startTime ? new Date(new Date(startTime).getTime() + 30 * 60_000).toISOString() : '',
                schedule_url: DEMO_SCHEDULE_URL,
                reschedule_url: DEMO_SCHEDULE_URL,
                cancel_url: DEMO_SCHEDULE_URL,
            };
            return HttpResponse.json([
                {
                    role_name: 'Member Relationship Manager',
                    specialist_name: DEMO_SPECIALIST_NAME,
                    image_url: '',
                    calendars: {
                        intro: calendar,
                        kickoff: calendar,
                        training: calendar,
                        offboarding: calendar,
                        mrm: calendar,
                        em: calendar,
                    },
                },
                {
                    role_name: 'Enrollment Manager',
                    specialist_name: DEMO_SPECIALIST_NAME,
                    image_url: '',
                    calendars: {
                        intro: calendar,
                        kickoff: calendar,
                        training: calendar,
                        offboarding: calendar,
                        mrm: calendar,
                        em: calendar,
                    },
                },
            ]);
        }),
        // Share link drives ReferralHero's "Copy invite link" button. Without this mock the
        // copyUrl resolves to null and the button is disabled.
        http.get('*/referral-share-link/', () =>
            HttpResponse.json({
                share_link: 'https://share.collective.com/demo',
                referral_code: 'DEMO123',
            })
        ),
        // useZDUser does `data.users[0]` and TanStack Query forbids returning undefined —
        // an empty array crashes with `Query data cannot be undefined` on first BTR render.
        http.get('*/zendesk/proxy/users/search.json', () =>
            HttpResponse.json({
                users: [
                    {
                        id: 999001,
                        name: `${profile.first_name} ${profile.last_name}`,
                        email: profile.email,
                        external_id: DEMO_MEMBER_UUID,
                    },
                ],
            })
        ),

        // Document upload target (Uppy XHRUpload). Returning a Document with a uuid lets the task
        // upload modal treat the upload as successful and proceed to complete the task.
        http.post('*/documents/member-documents/', () =>
            HttpResponse.json({
                uuid: 'demo-doc-bank-statement',
                id: 1,
                name: 'bank-statement.pdf',
                document_type: 'Business Bank Statement',
                source: 'local',
            })
        ),
        // --- S Corp election upload pipeline (ExtendAI) -------------------------------------------
        // Real flow: upload → classify → extract → validate-against-member. These mocks pass the happy
        // path for the demo persona; the trigger POSTs just succeed, the result is read from the GET.
        http.post('*/documents/:uuid/ocr/classify/', ({ params }) => {
            classificationStartedFor.add(String(params.uuid));
            return HttpResponse.json({ ok: true });
        }),
        http.post('*/documents/:uuid/ocr/data_extractions/', ({ params }) => {
            extractionStartedFor.add(String(params.uuid));
            return HttpResponse.json({ ok: true });
        }),
        http.get('*/documents/member-documents/:uuid/data-automation-job/', ({ params, request }) => {
            const jobType = new URL(request.url).searchParams.get('job_type');
            const uuid = String(params.uuid);
            // No job until the pipeline is triggered (on submit) — so the upload step shows rather
            // than auto-validating on mount. Only the S Corp election (Form 2553) reaches here.
            const started = jobType === 'CLASSIFIER' ? classificationStartedFor : extractionStartedFor;
            if (!started.has(uuid)) {
                return HttpResponse.json(null);
            }
            if (jobType === 'CLASSIFIER') {
                return HttpResponse.json({
                    uuid: 'demo-classifier-job',
                    status: 'SUCCESS',
                    job_type: 'CLASSIFIER',
                    provider_name: 'EXTENDAI',
                    job_result: { classified_document_type: 'FORM_2553', confidence: 0.97 },
                    failure_reason: '',
                    is_validated: false,
                    effective_raw_data: {},
                    raw_data: { classified_document_type: 'FORM_2553' },
                });
            }
            if (jobType === 'DATA_EXTRACTOR') {
                // Mirrors the demo member so every FORM_2553 validator passes.
                const jobResult = {
                    businessname: profile.organization,
                    ein: '12-3456789',
                    page_1_signature_present: true,
                    page_1_signature_is_digital: false,
                    page_1_date_of_signature_present: true,
                    page_2_all_shareholder_rows_have_signatures: true,
                    page_2_any_shareholder_signature_is_digital: false,
                    s_corp_election_date: '2022-03-15',
                    shareholders: [
                        {
                            ssn: '123-45-6789',
                            shareholder_identification_number: '123-45-6789',
                            ownership_percentage_or_shares: '100',
                            shareholder_signature_date: '2022-03-20',
                            fullname,
                        },
                    ],
                };
                return HttpResponse.json({
                    uuid: 'demo-extraction-job',
                    status: 'SUCCESS',
                    job_type: 'DATA_EXTRACTOR',
                    provider_name: 'EXTENDAI',
                    job_result: jobResult,
                    failure_reason: '',
                    is_validated: true,
                    effective_raw_data: jobResult,
                    raw_data: {},
                });
            }
            return HttpResponse.json(null);
        }),
        // Form 2553 download for the Print step → bundled sample PDF. Missing this only disables Print.
        http.get('*/documents/member-documents/:uuid/download_url/', () =>
            HttpResponse.json({ url: '/scorp-election/Example_Form_2553_Single.pdf' })
        ),
        // Address validation (formation address steps): echo the entered address back so it reads as
        // valid (no correction modal). The {ok:true} fallback lacks fields → "invalid information".
        http.post('*/addresses/validate/', async ({ request }) => {
            const body = (await request.json().catch(() => ({}))) as {
                street_address_1?: string;
                street_address_2?: string;
                city?: string;
                state?: string;
                zip_code?: string;
            };
            return HttpResponse.json({
                street_address_1: body.street_address_1 ?? '',
                street_address_2: body.street_address_2 ?? '-',
                city: body.city ?? '',
                state: body.state ?? '',
                zip_code: body.zip_code ?? '',
                zip_code_4: '0000',
                return_text: null,
            });
        }),
        // Business-name availability (formation): always available so Continue enables.
        http.post('*/opencorporates/verify-name/', () =>
            HttpResponse.json({ companies: [], message: '', status: 'AVAILABLE' })
        ),
        // Task workstreams use a DRF-paginated envelope, not a bare array.
        http.get('*/members/tasks/', () =>
            HttpResponse.json({
                total_record_count: 0,
                count: 0,
                next: null,
                previous: null,
                current_page: 1,
                current_cursor: '',
                results: [],
                total_tasks_count: 0,
            })
        ),
        http.get('*/launchpad/', () => HttpResponse.json({ items: [] })),
        http.get('*/launchpad/:slug/', () => HttpResponse.json({ items: [] })),
        // Incomplete during onboarding → hides the Accounting sub-items. Only queried in onboarding
        // stages, so inert for the onboarded view.
        http.get('*/bookkeeping/accounting-onboarding/progress/', () =>
            HttpResponse.json({
                uuid: 'demo-accounting-onboarding',
                created_at: '2026-06-01T00:00:00Z',
                updated_at: new Date().toISOString(),
                is_completed: false,
                completed_at: null,
                bank_connection_status: 'not_started',
                transaction_import_status: 'not_started',
                home_office_setup_status: 'not_started',
                other_document_upload_status: 'not_started',
            })
        ),
        http.get('*/tax-tracker/', () => HttpResponse.json({ trackers: [] })),
        http.get('*/itr-tracker/', () => HttpResponse.json({ trackers: [] })),
        http.get('*/btr-tracker/', () => HttpResponse.json({ trackers: [] })),
        http.get('*/quarterly-estimates/', () => HttpResponse.json([])),
        http.get('*/eoy-review/', () => HttpResponse.json({ status: 'not_started' })),

        // Bookkeeping — per-profile transactions, bank accounts, and reimbursements.
        // Read handlers pull from `state`, write handlers mutate it. State is per-tab
        // and resets on hard reload (see initBookkeepingState).
        http.post('*/bookkeeping/v2/transactions/search/', () =>
            HttpResponse.json({
                count: state.transactions.length,
                next: null,
                previous: null,
                next_cursor: null,
                previous_cursor: null,
                results: state.transactions,
            })
        ),
        http.post('*/bookkeeping/v2/transactions/summary/', () =>
            HttpResponse.json(summarizeTransactions(state.transactions))
        ),
        // Fetch-by-ids — called by handlePostMutationCacheUpdate right after each update
        // to surgically patch the table cache with fresh transaction rows. Storybook's
        // baseline returns `{ results: [] }`, which would make the cache patch early-return
        // and the user's category change never appear. Read from state and filter.
        http.get('*/bookkeeping/v2/transactions/', ({ request }) => {
            const url = new URL(request.url);
            const ids = url.searchParams.getAll('transaction_ids');
            const results = state.transactions.filter((t) =>
                ids.includes((t as { transaction_id?: string }).transaction_id ?? '')
            );
            return HttpResponse.json({ results });
        }),
        // Legacy GL-account update endpoint — used by the V2 TransactionCategory dropdown
        // (common/TransactionCategory.tsx) via useUpdateTransactionGLAccount. Without this
        // override the mutation echoes success but state never changes, so on invalidate
        // the table refetches state.transactions and the categorization snaps back.
        http.post('*/bookkeeping/member-transactions/update-gl-account/', async ({ request }) => {
            const body = (await request.json().catch(() => ({}))) as {
                transaction_uuid?: string;
                hurdlr_entity_id?: string;
                gl_account?: { id?: string; name?: string };
            };
            const txnId = body.transaction_uuid ?? body.hurdlr_entity_id;
            const tx = state.transactions.find((t) => (t as { transaction_id?: string }).transaction_id === txnId);
            if (tx && body.gl_account?.id) {
                const gl = findGl(body.gl_account.id);
                tx.gl_account = gl
                    ? { id: gl.id, name: gl.name }
                    : { id: body.gl_account.id, name: body.gl_account.name ?? 'Unknown' };
                // Categorizing a transaction also implicitly resolves the "needs review" flag.
                tx.review_status = 'RECONCILIATION_STAGED';
            }
            return HttpResponse.json({ ok: true });
        }),
        // Single-transaction update. Body may include gl_account_id, notes, designation,
        // review_approved. Storybook has a stateless echo handler for this — we win by
        // being earlier in the handler list (overrides → baseline → fallbacks).
        http.post('*/bookkeeping/v2/transactions/:transactionId/update/', async ({ request, params }) => {
            const body = (await request.json().catch(() => ({}))) as {
                gl_account_id?: string;
                notes?: string;
                designation?: 'business' | 'personal';
                review_approved?: boolean;
            };
            const transactionId = params.transactionId as string;
            const tx = state.transactions.find(
                (t) => (t as { transaction_id?: string }).transaction_id === transactionId
            );
            let glAccountResponse: { id: string; name: string } | undefined;
            if (tx) {
                if (body.gl_account_id !== undefined) {
                    const gl = findGl(body.gl_account_id);
                    const next = gl ? { id: gl.id, name: gl.name } : { id: body.gl_account_id, name: 'Unknown' };
                    tx.gl_account = next;
                    glAccountResponse = next;
                }
                if (body.notes !== undefined) {
                    tx.notes = body.notes;
                }
                if (body.designation !== undefined) {
                    tx.designation = { nature: body.designation };
                }
                if (body.review_approved) {
                    // Member explicitly approved → move toward reconciled-staged state.
                    tx.review_status = 'RECONCILIATION_STAGED';
                }
            }
            return HttpResponse.json({
                transaction_id: transactionId,
                status: 'synced',
                ...(glAccountResponse ? { gl_account: glAccountResponse } : {}),
            });
        }),
        // Bulk update — same shape but iterated. Echoes a per-item synced result back so
        // the bulk action bar resolves cleanly.
        http.post('*/bookkeeping/v2/transactions/bulk-update/', async ({ request }) => {
            const body = (await request.json().catch(() => ({ transactions: [] }))) as {
                transactions: Array<{
                    transaction_id: string;
                    gl_account_id?: string;
                    notes?: string;
                    designation?: 'business' | 'personal';
                    review_approved?: boolean;
                }>;
            };
            const results = (body.transactions ?? []).map((item) => {
                const tx = state.transactions.find(
                    (t) => (t as { transaction_id?: string }).transaction_id === item.transaction_id
                );
                let glAccountResponse: { id: string; name: string } | undefined;
                if (tx) {
                    if (item.gl_account_id !== undefined) {
                        const gl = findGl(item.gl_account_id);
                        const next = gl ? { id: gl.id, name: gl.name } : { id: item.gl_account_id, name: 'Unknown' };
                        tx.gl_account = next;
                        glAccountResponse = next;
                    }
                    if (item.notes !== undefined) {
                        tx.notes = item.notes;
                    }
                    if (item.designation !== undefined) {
                        tx.designation = { nature: item.designation };
                    }
                    if (item.review_approved) {
                        tx.review_status = 'RECONCILIATION_STAGED';
                    }
                }
                return {
                    item_id: item.transaction_id,
                    status: 'synced' as const,
                    ...(glAccountResponse ? { gl_account: glAccountResponse } : {}),
                };
            });
            return HttpResponse.json({
                synced_count: results.length,
                pending_count: 0,
                failed_count: 0,
                results,
            });
        }),
        http.get('*/bookkeeping/v2/bank-accounts/', () => {
            const accounts = blob.bookkeeping?.bank_accounts ?? [];
            return HttpResponse.json({
                count: accounts.length,
                next: null,
                previous: null,
                next_cursor: null,
                previous_cursor: null,
                results: accounts,
            });
        }),
        // Settings → Bank accounts, and the landing page for the "connection was lost" task card.
        // The Storybook baseline returns [], which renders the "Connect account" zero state; the demo
        // member already has accounts, so build the Plaid items from the same content blob the rest of
        // bookkeeping reads. The lost-connection institution carries Plaid's error fields so the page
        // shows the real red alert the task card is talking about. Its "Reconnect" button opens Plaid
        // Link — a no-op in the demo, like every other third-party handoff.
        http.get('*/hurdlr/member/plaid-items/', () => HttpResponse.json(buildPlaidItems(blob))),
        http.get('*/bookkeeping/v2/gl-accounts/', () =>
            HttpResponse.json({
                count: DEMO_GL_ACCOUNTS.length,
                next: null,
                previous: null,
                next_cursor: null,
                previous_cursor: null,
                results: DEMO_GL_ACCOUNTS,
            })
        ),
        // IRS mileage rates — without this the Accountable Plan's mileage line computes to $0.
        http.get('*/bookkeeping/irsmileagerates/', () =>
            HttpResponse.json([
                { id: 1, rate: '0.67', effective_date: '2024-01-01' },
                { id: 2, rate: '0.70', effective_date: '2025-01-01' },
            ])
        ),
        // Accountable plans (reimbursements). OOP items carry an `accountable_plan_uuid`
        // field; the GET filters by it so each plan only sees its own items.
        http.get('*/bookkeeping/accountable-plans/', () => HttpResponse.json(state.accountable_plans)),
        http.get('*/bookkeeping/accountable-plans/:uuid/out-of-pocket-transactions', ({ params }) => {
            const scoped = state.out_of_pocket.filter(
                (item) => (item as { accountable_plan_uuid?: string }).accountable_plan_uuid === params.uuid
            );
            return HttpResponse.json(scoped);
        }),
        // Plan update — PUT with the full plan body.
        http.put('*/bookkeeping/accountable-plans/:uuid/', async ({ request, params }) => {
            const body = (await request.json().catch(() => ({}))) as Record<string, unknown>;
            const plan = state.accountable_plans.find((p) => (p as { uuid?: string }).uuid === params.uuid);
            if (plan) {
                Object.assign(plan, body, { updated_at: new Date().toISOString() });
                return HttpResponse.json(plan);
            }
            return HttpResponse.json({ detail: 'Not found' }, { status: 404 });
        }),
        http.post('*/bookkeeping/accountable-plans/:uuid/submit/', ({ params }) => {
            const plan = state.accountable_plans.find((p) => (p as { uuid?: string }).uuid === params.uuid);
            if (plan) {
                plan.status = 'submitted';
                plan.updated_at = new Date().toISOString();
                return HttpResponse.json(plan);
            }
            return HttpResponse.json({ detail: 'Not found' }, { status: 404 });
        }),
        http.post('*/bookkeeping/accountable-plans/:uuid/skip/', ({ params }) => {
            const plan = state.accountable_plans.find((p) => (p as { uuid?: string }).uuid === params.uuid);
            if (plan) {
                plan.status = 'skipped';
                plan.updated_at = new Date().toISOString();
                return HttpResponse.json(plan);
            }
            return HttpResponse.json({ detail: 'Not found' }, { status: 404 });
        }),
        // Out-of-pocket transactions. The route param is the accountable plan uuid; for
        // updates/deletes the path also carries the OOP transaction uuid.
        http.post('*/bookkeeping/accountable-plans/:uuid/out-of-pocket-transactions', async ({ request, params }) => {
            const body = (await request.json().catch(() => ({}))) as Record<string, unknown>;
            const now = new Date().toISOString();
            const created = {
                ...body,
                accountable_plan_uuid: (body.accountable_plan_uuid as string) ?? (params.uuid as string),
                transaction_uuid: nextOopUuid(),
                created_at: now,
                updated_at: now,
            };
            state.out_of_pocket.push(created);
            return HttpResponse.json(created);
        }),
        http.patch(
            '*/bookkeeping/accountable-plans/:uuid/out-of-pocket-transactions/:txnUuid',
            async ({ request, params }) => {
                const body = (await request.json().catch(() => ({}))) as Record<string, unknown>;
                const item = state.out_of_pocket.find(
                    (o) => (o as { transaction_uuid?: string }).transaction_uuid === params.txnUuid
                );
                if (item) {
                    Object.assign(item, body, { updated_at: new Date().toISOString() });
                    return HttpResponse.json(item);
                }
                return HttpResponse.json({ detail: 'Not found' }, { status: 404 });
            }
        ),
        http.delete('*/bookkeeping/accountable-plans/:uuid/out-of-pocket-transactions/:txnUuid', ({ params }) => {
            const before = state.out_of_pocket.length;
            state.out_of_pocket = state.out_of_pocket.filter(
                (o) => (o as { transaction_uuid?: string }).transaction_uuid !== params.txnUuid
            );
            if (state.out_of_pocket.length === before) {
                return HttpResponse.json({ detail: 'Not found' }, { status: 404 });
            }
            return new HttpResponse(null, { status: 204 });
        }),

        // Payroll — mocks for the prod Payroll module so it renders the activated dashboard.
        // Only the S-Corp persona reaches the Payroll page via the sidebar (LLC gets
        // gusto.connected=false above, so their sidebar omits Payroll entirely). These handlers
        // stay unbranched: if a non-S-Corp visitor lands on /payroll directly (e.g. via the
        // Try-It checklist link), the page short-circuits at the disconnected-Gusto zero state
        // before any of these endpoints get called, so the mocks just don't fire.
        http.get('*/payroll/service/', () =>
            HttpResponse.json({
                gep_eligible: true,
                migrate_date: '2024-01-15',
            })
        ),
        http.get('*/payroll/setup-progress/', () => HttpResponse.json({ status: 'completed' })),
        http.get('*/payroll/pay-schedule-overview/', () =>
            HttpResponse.json({
                frequency: 'Twice per month',
                day_1: 11,
                day_2: 25,
                auto_payroll: true,
            })
        ),
        http.get('*/payroll/reasonable-compensations/', () => HttpResponse.json({})),
        http.get('*/payroll/reasonable-compensations/financial-data/', () => HttpResponse.json({})),

        http.get('*/gusto/companies/', () =>
            HttpResponse.json({
                uuid: DEMO_GUSTO_COMPANY_UUID,
                name: profile.organization,
                company_status: 'Approved',
                is_suspended: false,
                trade_name: profile.organization,
                ein: '12-3456789',
            })
        ),
        http.get(`*/gusto/companies/${DEMO_GUSTO_COMPANY_UUID}/pay-schedules/`, () =>
            HttpResponse.json([
                {
                    uuid: 'pay_schedule_demo',
                    version: '1',
                    frequency: 'Twice per month',
                    day_1: 11,
                    day_2: 25,
                    auto_payroll: true,
                    custom_name: 'Semi-monthly',
                },
            ])
        ),
        http.get(`*/gusto/companies/${DEMO_GUSTO_COMPANY_UUID}/payrolls/`, () =>
            HttpResponse.json([
                {
                    payroll_uuid: 'payroll_current',
                    payroll_type: 'Regular',
                    start_date: '2026-05-12',
                    end_date: '2026-05-25',
                    check_date: '2026-05-22',
                    company_debit_date: '2026-05-18',
                    company_debit: '3893.37',
                    processed: true,
                    processed_date: '2026-05-15T10:00:00Z',
                    // String must match PAYROLL_STATUS values (capitalized) — the next/previous
                    // getters and PayrollStatus branches compare against these literals.
                    status: 'Pending Payment',
                    payroll_deadline: '2026-05-15',
                    utc_deadline: '2026-05-15T20:00:00Z',
                    employee_compensations: [],
                },
                {
                    payroll_uuid: 'payroll_previous',
                    payroll_type: 'Regular',
                    start_date: '2026-04-26',
                    end_date: '2026-05-11',
                    check_date: '2026-05-11',
                    company_debit_date: '2026-05-05',
                    company_debit: '3893.37',
                    processed: true,
                    processed_date: '2026-05-08T10:00:00Z',
                    status: 'Paid',
                    payroll_deadline: '2026-05-08',
                    utc_deadline: '2026-05-08T20:00:00Z',
                    employee_compensations: [],
                },
            ])
        ),
        // Payroll receipt modal reads totals.company_debit with no null-guard, so a missing/partial
        // response crashes it — return a complete receipt. Amount mirrors the payrolls list above.
        http.get('*/gusto/payrolls/:uuid/receipt/', () =>
            HttpResponse.json({
                totals: { company_debit: '3893.37' },
                name_of_sender: profile.organization,
                name_of_recipient: 'Gusto, Inc.',
                recipient_notice: 'Funds are debited from your connected business bank account.',
                debit_date: '2026-05-18',
                license: 'Gusto Capital, LLC — NMLS #1845463',
                license_uri: 'https://gusto.com/about/licenses',
                licensee: {
                    address: '525 20th Street',
                    city: 'San Francisco',
                    state: 'CA',
                    postal_code: '94107',
                    phone_number: '(800) 936-0383',
                },
            })
        ),
        http.get(`*/gusto/companies/${DEMO_GUSTO_COMPANY_UUID}/employees/`, () => HttpResponse.json([])),
        http.get('*/gusto/salary/member/', () =>
            HttpResponse.json({
                rate: 45000,
                payment_unit: 'Year',
                job_uuid: 'job_demo',
                recommended_low: 38000,
                recommended_high: 58000,
                calculated_date: '2026-01-15',
            })
        ),
        http.get('*/gusto/companies/signatory/', () => HttpResponse.json({})),
        http.get('*/gusto/companies/tos-status/', () => HttpResponse.json({ latest_terms_accepted: true })),
        http.get('*/gusto/embedded/company-onboarding-status/', () =>
            HttpResponse.json({ onboarding_completed: true })
        ),
        http.get('*/gusto/embedded/employee-onboarding-status/', () =>
            HttpResponse.json({ onboarding_completed: true })
        ),
        http.get('*/gusto/payrolls/blockers/', () => HttpResponse.json([])),

        // Insurance — make the demo member appear onboarded to a relevant policy. The prod
        // Insurance page is mostly native React (only the CoverDash quote/BOR flows are iframes);
        // returning one active policy switches the page from the empty "Recommended" upsell to
        // the populated "Active policies" dashboard.
        http.get('*/insurance/policies/', () => {
            const policy = (blob as { insurance?: { policy?: unknown } }).insurance?.policy;
            return HttpResponse.json(policy ? [policy] : []);
        }),
        http.get('*/insurance/applications/', () => HttpResponse.json([])),
        http.get('*/insurance/summary/', () =>
            HttpResponse.json({
                has_profile: true,
                vendor: 'coverdash',
                external_id: 'biz_demo_insurance',
                latest_application: null,
                can_get_quotes: true,
            })
        ),

        // Taxes — populate the Quarterly Estimates landing page with the *current* quarter's QTE plus
        // the year's earlier quarters as completed, so the prod component renders the full dashboard
        // instead of its "no QTE yet" placeholder. Derived from the same `getCurrentQTE()` the page
        // uses (never hardcoded to a quarter — that placeholder is exactly what a stale fixture
        // caused once Q2 passed). ITR/BTR organizer routes remain unmocked.
        http.get('*/qte/member-qte/', ({ request }) => {
            const requestedYear = Number(new URL(request.url).searchParams.get('year') ?? demoQteYear);
            if (requestedYear !== demoQteYear) {
                return HttpResponse.json([]);
            }
            // Newest first, like the API. Figures ramp by quarter so the year reads as a growing book.
            return HttpResponse.json(
                range(demoQteQuarter, 0, -1).map((quarter) =>
                    buildDemoQte({
                        year: demoQteYear,
                        quarter,
                        netIncome: String(60000 + quarter * 30000),
                        grossWages: String(15000 + quarter * 15000),
                        federal: 2400 + quarter * 400,
                        californiaStateTax: 760 + quarter * 120,
                        // The current quarter is estimated but unpaid (`estimate_completed` is what
                        // makes the Taxes-due cards show real amounts instead of "No estimates or
                        // payments yet"); earlier quarters are paid and closed.
                        status: quarter === demoQteQuarter ? 'estimate_completed' : 'completed',
                    })
                )
            );
        }),
        // Tax payments list (useListTaxPayments). Without this the request falls to the catch-all
        // 204, so callers that do payments.forEach(...) crash the Taxes pages. Return an array.
        http.get('*/qte/member-tax-payments/', () => HttpResponse.json([])),
        /*
         * Drives the state row on the Quarterly Estimates page.
         *
         * Must match `TaxJurisdictionInfo` field for field. This previously returned an
         * invented shape (`state` / `has_estimated_tax` / `state_name`) that no production
         * code has ever read; the app decides whether a state levies income tax from an
         * EMPTY `qte_payment_portal_url`, so every demo state — California included — was
         * telling prospects it collects no state income tax (CA-5697).
         *
         * An unresolved lookup is `undefined` and reads as "assume taxing", but this object
         * is truthy, so a missing field here is not a soft failure: it asserts the opposite.
         */
        http.get('*/qte/tax-jurisdiction-info/', () =>
            HttpResponse.json({
                jurisdiction: stateInfo.name,
                tax_form: '',
                agi_line_reference: '',
                tax_liability_line_reference: '',
                // '' only for the nine no-income-tax states, which is what suppresses their row.
                qte_payment_portal_url: getStatePaymentPortalUrl(stateCode),
                state_entity_tax_payment_portal_url: '',
                // Empty as the Storybook baseline is: the Fees and Franchise tab stays empty
                // rather than quoting a franchise-tax figure nobody has reviewed. Cast because
                // the interface types this as a 1-tuple, which no consumer relies on.
                business_taxes: [] as unknown as TaxJurisdictionInfo['business_taxes'],
                // `satisfies`, not a bare object: this is the guard that makes a future field
                // rename a type error here instead of a wrong sentence on a prospect's screen.
            } satisfies TaxJurisdictionInfo)
        ),
        http.get('*/tax-profile/', () =>
            HttpResponse.json({
                // Capitalized to match `FilingStatuses` — `isRemittanceEligibleState` compares
                // against the literal 'Single', so lowercase silently denied the California demo
                // the in-app "Pay now" path and fell it back to the external portal.
                federal_filing_status: 'Single',
                state_filing_status: 'Single',
                state_of_residency: { id: stateCode, name: stateInfo.name },
                recent_review_completed: null,
                shareholders: [],
                dependents: [],
                nexus: [],
                foreign_registrations: [],
                qbo_connection: null,
                gusto_connection: null,
                bookkeeping_connection: null,
            })
        ),
        http.get('*/members/dependents/', () => HttpResponse.json([])),

        // BTR + ITR — populate the prod MainPage components so they render the "return
        // accepted" final state. Status objects below correspond to a fully filed return:
        // organizer completed, draft signed, final accepted by the IRS.
        http.get('*/btr/member/business_tax_return/status/', () =>
            HttpResponse.json({
                btr_id: 'btr_demo_2025',
                opted_out: false,
                tax_season_started: true,
                show_previous_years: true,
                organizer_status: 'completed',
                organizer_due_date: '2026-03-16',
                draft_review_status: 'signed_and_approved',
                draft_review_due_date: '2026-03-10',
                expected_draft_delivery_date: null,
                draft_review_signed_date: '2026-03-03',
                final_return_status: 'completed',
                final_return_due_date: '2026-03-16',
                current_itr_purchased: true,
                current_itr_completed: false,
                is_multi_state: false,
                is_extended: false,
                extension_filing_deadline: null,
                extension_payment_due_date: null,
                extension_payment_estimates: null,
                extension_estimates_as_of_date: null,
                extension_estimates_status: null,
            })
        ),
        http.get('*/itr/member/individual_tax_return/status/', () =>
            HttpResponse.json({
                package_purchased: true,
                opted_out: false,
                tax_season_started: true,
                show_previous_years: true,
                preparation_path: 'in_house',
                organizer_status: 'completed',
                organizer_due_date: '2026-04-15',
                draft_review_status: 'signed_and_approved',
                draft_review_due_date: '2026-04-10',
                final_return_status: 'completed',
                final_return_due_date: '2026-04-15',
                current_qte_quarter: demoQteQuarter,
                current_qte_completed: false,
                taxes_due: [],
                consent_accepted: true,
            })
        ),
        // Document types catalog — drives DocumentTypeName label lookups in the tax doc tables.
        // Without this, rows fall back to the raw type ID (e.g. "BUSINESS_TAX_RETURN_FINAL").
        http.get('*/documents/document-types/', () => {
            const types: Array<[string, string, string]> = [
                // Tax
                ['BUSINESS_TAX_RETURN_FINAL', 'Business Tax Return (Final)', 'TAX'],
                ['BUSINESS_TAX_RETURN_EFILE_SIGNATURE', 'BTR Efile Signature', 'TAX'],
                ['SCHEDULE_K-1', 'Schedule K-1', 'TAX'],
                ['TAX_ORGANIZER', 'Tax Organizer', 'TAX'],
                ['W-2', 'W-2', 'TAX'],
                ['W-2c', 'W-2c', 'TAX'],
                ['INDIVIDUAL_TAX_RETURN_FINAL', 'Individual Tax Return (Final)', 'TAX'],
                ['INDIVIDUAL_TAX_RETURN_DRAFT', 'Individual Tax Return — Draft', 'TAX'],
                ['1099-DIV', '1099-DIV', 'TAX'],
                ['1099-B', '1099-B', 'TAX'],
                ['1099-MISC', '1099-MISC', 'TAX'],
                ['1099-NEC', '1099-NEC', 'TAX'],
                // Business formation
                ['ARTICLES_OF_INCORPORATION', 'Articles of Incorporation', 'FORMATION'],
                ['OPERATING_AGREEMENT', 'Operating Agreement', 'FORMATION'],
                ['EIN_LETTER', 'EIN Letter', 'FORMATION'],
                ['S_CORP_ELECTION_LETTER', 'S-Corp Election Letter', 'FORMATION'],
                ['BUSINESS_LICENSE', 'Business License', 'FORMATION'],
                // Accounting / bookkeeping
                ['BANK_STATEMENT', 'Bank Statement', 'ACCOUNTING'],
                ['ACCOUNTABLE_PLAN', 'Accountable Plan', 'ACCOUNTING'],
                ['RECEIPT', 'Receipt', 'ACCOUNTING'],
                // Payroll
                ['PAYROLL_REPORT', 'Payroll Report', 'PAYROLL'],
                // Insurance
                ['INSURANCE_POLICY', 'Insurance Policy', 'INSURANCE'],
                ['OTHER', 'Other', 'OTHER'],
            ];
            return HttpResponse.json(
                types.map(([id, label, category]) => ({
                    id,
                    label,
                    created_at: '2024-01-01T00:00:00Z',
                    updated_at: '2024-01-01T00:00:00Z',
                    is_cold_storage: false,
                    is_deprecated: false,
                    is_internal: false,
                    product_categories: [category],
                    requires_validation: false,
                }))
            );
        }),
        // Documents search powers the BTR/ITR "View return" preview + the TaxDocumentsSection
        // doc table. Returns a static mix of BTR/ITR-style documents on every query — server-side
        // filter logic is skipped, and the column renderer formats whichever rows it gets.
        http.post('*/documents/search', () => {
            const baseDate = Date.parse('2026-03-04T10:00:00Z');
            const mkDoc = (
                uuid: string,
                type: string,
                fileName: string,
                daysOffset: number,
                by: string,
                category = 'TAX',
                year: number | null = 2025
            ) => {
                const created = new Date(baseDate - daysOffset * 86400_000).toISOString();
                const [firstName, lastName] = by.split(' ');
                return {
                    uuid,
                    id: uuid,
                    member: { uuid: DEMO_MEMBER_UUID, id: 1 },
                    document_type: type,
                    document_label: fileName,
                    document_type_data: {
                        id: type,
                        label: type,
                        created_at: '2024-01-01T00:00:00Z',
                        updated_at: '2024-01-01T00:00:00Z',
                        is_cold_storage: false,
                        is_deprecated: false,
                        is_internal: false,
                        product_categories: [category],
                        requires_validation: false,
                    },
                    file_name: fileName,
                    file_format: fileName.split('.').pop() ?? 'pdf',
                    created_at: created,
                    updated_at: created,
                    created_by: { id: 2, first_name: firstName ?? 'Collective', last_name: lastName ?? 'Team' },
                    product_categories: [category],
                    year,
                    metadata: {},
                };
            };
            const results = [
                // Tax — 2025 filings
                mkDoc(
                    'doc_btr_final',
                    'BUSINESS_TAX_RETURN_FINAL',
                    '2025 Business Tax Return.pdf',
                    0,
                    'Collective Team'
                ),
                mkDoc('doc_btr_efile', 'BUSINESS_TAX_RETURN_EFILE_SIGNATURE', 'btr-efile-signed.pdf', 1, 'Pat Acme'),
                mkDoc('doc_k1', 'SCHEDULE_K-1', '2025 Schedule K-1.pdf', 0, 'Collective Team'),
                mkDoc('doc_tax_org', 'TAX_ORGANIZER', 'tax_organizer_2025.pdf', 14, 'Pat Acme'),
                mkDoc('doc_w2', 'W-2', '2025 W-2.pdf', 25, 'Collective Team'),
                mkDoc(
                    'doc_itr_final',
                    'INDIVIDUAL_TAX_RETURN_FINAL',
                    '2025 Individual Tax Return.pdf',
                    -10,
                    'Collective Team'
                ),
                mkDoc('doc_itr_draft', 'INDIVIDUAL_TAX_RETURN_DRAFT', 'Draft 2025 ITR v2.pdf', -17, 'Collective Team'),
                mkDoc('doc_1099_div', '1099-DIV', 'broker-1099-div.pdf', -18, 'Pat Acme'),
                mkDoc('doc_1099_b', '1099-B', 'broker-1099-b.pdf', -18, 'Pat Acme'),

                // Business formation
                mkDoc(
                    'doc_articles',
                    'ARTICLES_OF_INCORPORATION',
                    'Articles of Incorporation - Acme Consulting Inc.pdf',
                    800,
                    'Pat Acme',
                    'FORMATION',
                    null
                ),
                mkDoc(
                    'doc_oa',
                    'OPERATING_AGREEMENT',
                    'Operating Agreement - signed.pdf',
                    795,
                    'Pat Acme',
                    'FORMATION',
                    null
                ),
                mkDoc(
                    'doc_ein',
                    'EIN_LETTER',
                    'IRS CP575 - EIN Confirmation.pdf',
                    793,
                    'Collective Team',
                    'FORMATION',
                    null
                ),
                mkDoc(
                    'doc_scorp',
                    'S_CORP_ELECTION_LETTER',
                    '2553 Acceptance Letter - 2022.pdf',
                    770,
                    'Collective Team',
                    'FORMATION',
                    2022
                ),
                mkDoc(
                    'doc_license',
                    'BUSINESS_LICENSE',
                    'San Francisco Business Registration 2026.pdf',
                    30,
                    'Pat Acme',
                    'FORMATION',
                    2026
                ),

                // Accounting — recent bank statements + accountable plan + receipts
                mkDoc(
                    'doc_bs_apr',
                    'BANK_STATEMENT',
                    'Wells Fargo - Apr 2026 Statement.pdf',
                    -28,
                    'Collective Team',
                    'ACCOUNTING',
                    2026
                ),
                mkDoc(
                    'doc_bs_mar',
                    'BANK_STATEMENT',
                    'Wells Fargo - Mar 2026 Statement.pdf',
                    0,
                    'Collective Team',
                    'ACCOUNTING',
                    2026
                ),
                mkDoc(
                    'doc_bs_feb',
                    'BANK_STATEMENT',
                    'Wells Fargo - Feb 2026 Statement.pdf',
                    30,
                    'Collective Team',
                    'ACCOUNTING',
                    2026
                ),
                mkDoc(
                    'doc_chase_apr',
                    'BANK_STATEMENT',
                    'Chase Business Savings - Apr 2026.pdf',
                    -28,
                    'Collective Team',
                    'ACCOUNTING',
                    2026
                ),
                mkDoc(
                    'doc_ap_apr',
                    'ACCOUNTABLE_PLAN',
                    'Accountable Plan - April 2026.pdf',
                    -20,
                    'Pat Acme',
                    'ACCOUNTING',
                    2026
                ),
                mkDoc(
                    'doc_ap_mar',
                    'ACCOUNTABLE_PLAN',
                    'Accountable Plan - March 2026.pdf',
                    8,
                    'Pat Acme',
                    'ACCOUNTING',
                    2026
                ),
                mkDoc('doc_rec_wework', 'RECEIPT', 'WeWork May invoice.pdf', -16, 'Pat Acme', 'ACCOUNTING', 2026),
                mkDoc('doc_rec_aws', 'RECEIPT', 'AWS invoice 2026-04.pdf', -22, 'Pat Acme', 'ACCOUNTING', 2026),
                mkDoc(
                    'doc_rec_adobe',
                    'RECEIPT',
                    'Adobe Creative Cloud renewal.pdf',
                    -19,
                    'Pat Acme',
                    'ACCOUNTING',
                    2026
                ),
                mkDoc(
                    'doc_rec_delta',
                    'RECEIPT',
                    'Delta - client offsite Apr 12.pdf',
                    -33,
                    'Pat Acme',
                    'ACCOUNTING',
                    2026
                ),

                // Payroll
                mkDoc(
                    'doc_payroll_apr',
                    'PAYROLL_REPORT',
                    'Payroll Report - April 2026.pdf',
                    -22,
                    'Collective Team',
                    'PAYROLL',
                    2026
                ),
                mkDoc(
                    'doc_payroll_mar',
                    'PAYROLL_REPORT',
                    'Payroll Report - March 2026.pdf',
                    6,
                    'Collective Team',
                    'PAYROLL',
                    2026
                ),

                // Insurance
                mkDoc(
                    'doc_policy_pl',
                    'INSURANCE_POLICY',
                    'Professional Liability Policy - Hiscox 2026.pdf',
                    -55,
                    'Collective Team',
                    'INSURANCE',
                    2026
                ),
            ];
            return HttpResponse.json({
                results,
                // DocumentList.tsx destructures `total_records` — if it's undefined, the page
                // renders <NoDocuments /> even when results has entries. Send every variant the
                // codebase reads so we're robust to future renames.
                total_records: results.length,
                total_record_count: results.length,
                count: results.length,
                current_page: 1,
                next: null,
                previous: null,
                current_cursor: '',
            });
        }),
        // Product categories — drives the category filter chips on the Documents page.
        http.get('*/documents/product-categories/', () =>
            HttpResponse.json([
                { id: 'TAX', label: 'Tax', created_at: '2024-01-01T00:00:00Z', updated_at: '2024-01-01T00:00:00Z' },
                {
                    id: 'FORMATION',
                    label: 'Business Formation',
                    created_at: '2024-01-01T00:00:00Z',
                    updated_at: '2024-01-01T00:00:00Z',
                },
                {
                    id: 'ACCOUNTING',
                    label: 'Accounting',
                    created_at: '2024-01-01T00:00:00Z',
                    updated_at: '2024-01-01T00:00:00Z',
                },
                {
                    id: 'PAYROLL',
                    label: 'Payroll',
                    created_at: '2024-01-01T00:00:00Z',
                    updated_at: '2024-01-01T00:00:00Z',
                },
                {
                    id: 'INSURANCE',
                    label: 'Insurance',
                    created_at: '2024-01-01T00:00:00Z',
                    updated_at: '2024-01-01T00:00:00Z',
                },
            ])
        ),
        // BTR/ITR summary — feeds BTRRefundsTaxesDueCard + ITRRefundsTaxesDueCard. The cards
        // call useBTRJurisdictions / useITRJurisdictions, which access `summary.federal.*`
        // unconditionally once data is present — an empty `[]` fallback crashes with
        // "Cannot read properties of undefined (reading 'refund_amount')".
        http.get('*/btr/member/business_tax_return/:btrUuid/summary/', () =>
            HttpResponse.json({
                gross_receipts: '184500.00',
                total_income: '184500.00',
                total_deductions: '62400.00',
                net_income: '122100.00',
                federal: { amount_due: '0.00', refund_amount: '0.00' },
                // The authored 800.00 is California's franchise tax. Other states' business
                // filing fees differ and the demo has no table for them, so the figure is
                // rate-scaled for plausibility and dropped entirely where there is no state
                // income tax (those states also have no comparable return here).
                states: buildStateJurisdictions(800),
                notes_for_member: 'Please let us know if you have any questions!',
                payment_due_date: '2026-03-16',
            })
        ),
        http.get('*/itr/member/individual_tax_return/:itrUuid/summary/', () =>
            HttpResponse.json({
                total_deductions_and_credits: '24800.00',
                adjusted_gross_income: '142100.00',
                total_tax_liability: '28400.00',
                federal: { amount_due: '28400.00', refund_amount: '0.00' },
                states: buildStateJurisdictions(4200),
            })
        ),
        // ITR list — useITRJurisdictions picks the first entry's id to fetch the summary.
        http.get('*/itr/member/individual_tax_return/', () =>
            HttpResponse.json({
                results: [
                    {
                        id: 'itr_demo_2025',
                        member: 1,
                        tax_year: 2025,
                        created_at: '2026-01-15T00:00:00Z',
                        updated_at: '2026-04-10T00:00:00Z',
                        is_organizer_locked: true,
                    },
                ],
            })
        ),
        // Payment info — useBTRPaymentInfoQueries fires once per jurisdiction with taxes_due.
        // Unmocked it falls through to the empty-array fallback which is a shape mismatch.
        http.get('*/btr/member/business_tax_return/payment-info/', () =>
            HttpResponse.json({ payment_url: 'https://example.com/pay' })
        ),
        // CSat survey request — BTR / ITR completed states mount CSatSurvey which fires a
        // POST to fetch the survey id. Returning a "no survey available" response keeps the
        // popup dormant and stops the retry loop on the fallback `{ok: true}` shape.
    ];
}

/**
 * Last-resort fallback handlers — these MUST run after every specific override and after the
 * Storybook handler set, so a real handler always wins. Catches anything that hits the
 * configured Django host and returns a neutral value:
 *   - GET → empty array (matches the shape of most list endpoints; `.filter`/`.map` are safe)
 *   - POST/PUT/PATCH/DELETE → `{ ok: true }` so the demo never crashes on write attempts.
 *
 * Scoped to djangoOrigin so they don't intercept Vite source modules served from the demo
 * origin (a previous version of these handlers was too broad and grabbed `/src/...` requests).
 */
export function buildFallbackHandlers(): RequestHandler[] {
    const djangoOrigin = getDjangoOrigin();
    return [
        http.get(`${djangoOrigin}/*`, () => HttpResponse.json([])),
        http.post(`${djangoOrigin}/*`, () => HttpResponse.json({ ok: true })),
        http.put(`${djangoOrigin}/*`, () => HttpResponse.json({ ok: true })),
        http.patch(`${djangoOrigin}/*`, () => HttpResponse.json({ ok: true })),
        http.delete(`${djangoOrigin}/*`, () => HttpResponse.json({ ok: true })),
    ];
}

/**
 * `/trackevents/` is the one call allowed out to the real backend (demo analytics) — without this
 * the catch-all below would fake-204 it. Matches any origin (same-origin or absolute URL).
 */
export function buildAnalyticsPassthroughHandler(): RequestHandler {
    return http.post('*/trackevents/', () => passthrough());
}

/**
 * Final catch-all so nothing escapes to the real network. Anything not matched by an
 * override, the Storybook baseline, or the Django fallbacks above lands here.
 *
 *   - Same-origin page navigations + static assets / Vite internals → passthrough so the
 *     HTML, JS/CSS chunks, source modules, and the MSW worker file load normally.
 *   - Everything else → 204 No Content. This covers cross-origin calls (real Stripe /
 *     Datadog / Segment / Amplitude) AND same-origin API calls — which appear if
 *     VITE_DJANGO_URL is unset and collectiveApi falls back to relative URLs pointed at
 *     the demo's own host. Either way, no fake-user request reaches a real backend.
 */
export function buildCatchAllHandler(): RequestHandler {
    return http.all('*', ({ request }) => {
        const url = new URL(request.url);
        const isDocOrAsset =
            request.mode === 'navigate' ||
            /\.[^/]+$/.test(url.pathname) ||
            url.pathname.startsWith('/@') ||
            url.pathname.startsWith('/node_modules/') ||
            url.pathname.startsWith('/src/') ||
            url.pathname.startsWith('/demo/');
        if (url.origin === window.location.origin && isDocOrAsset) {
            return passthrough();
        }
        return new HttpResponse(null, { status: 204 });
    });
}
